import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { AuthShell } from './ui/AuthShell';
import {
  usernameService,
  validateUsernameFormat,
} from '../services/usernameService';

interface OnboardingUsernameScreenProps {
  userId: string;
  onUsernameSet: (username: string) => void;
  onSignOut: () => Promise<void>;
}

export function OnboardingUsernameScreen({
  userId,
  onUsernameSet,
  onSignOut,
}: OnboardingUsernameScreenProps) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = username.trim();
    const formatError = validateUsernameFormat(trimmed);
    if (formatError) {
      setError(formatError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const avail = await usernameService.checkAvailability(trimmed, userId);
      if (!avail.available) {
        setError(avail.message || 'Username is already taken.');
        setSubmitting(false);
        return;
      }

      const updated = await usernameService.changeUsername(userId, trimmed);
      onUsernameSet(updated);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to save username. Please try again.';
      setError(msg);
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await onSignOut();
    } catch {
      setError('Unable to sign out. Please try again.');
      setSigningOut(false);
    }
  };

  return (
    <AuthShell title="Choose your username">
      <Box sx={{ textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Create a unique username for your qkIndex account. Usernames must be
          3–20 characters long and can only contain letters, numbers,
          underscores, and hyphens.
        </Typography>

        <Box
          component="form"
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          sx={{ display: 'grid', gap: 2 }}
        >
          <TextField
            label="Username"
            value={username}
            onChange={(e) => handleUsernameChange(e.target.value)}
            placeholder="e.g. alex_123"
            fullWidth
            autoFocus
            disabled={submitting || signingOut}
            inputProps={{ maxLength: 20 }}
          />

          {error && (
            <Alert severity="error" role="alert" sx={{ textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting || signingOut || !username.trim()}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue'
            )}
          </Button>

          <Button
            type="button"
            fullWidth
            disabled={submitting || signingOut}
            onClick={() => {
              void handleSignOut();
            }}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </Button>
        </Box>
      </Box>
    </AuthShell>
  );
}
